import React, { HTMLAttributes } from "react";
import cn from "classnames";
import {
    AriaTooltipProps,
    TooltipTriggerProps,
    useTooltip,
    useTooltipTrigger,
} from "@react-aria/tooltip";
import { mergeProps } from "@react-aria/utils";
import { TooltipTriggerState, useTooltipTriggerState } from "react-stately";
// SCSS
import "./ToolTip.scss";
import { Overlay, useOverlayPosition } from "@react-aria/overlays";
// Interfaces
export interface TooltipProps {
    className?: string;
    /**
     * The direct child should be a button or link to be able to trigger the
     * tooltip
     */
    children: React.ReactNode;

    state?: TooltipTriggerState;
    /**
     * Ref of the tooltip
     */
    tooltipRef: React.RefObject<HTMLSpanElement>;
    /**
     * Ref of the element related to tooltip
     */
    targetRef: React.RefObject<HTMLElement>;
}
const Tooltip = ({
    children,
    className,
    state,
    tooltipRef,
    targetRef,
    ...props
}: TooltipProps & AriaTooltipProps) => {
    let { tooltipProps } = useTooltip(props, state);

    return (
        <span
            className={cn("ds-tooltip", className)}
            {...mergeProps(props, tooltipProps)}
            ref={tooltipRef}
            style={getTooltipPosition(targetRef)}
        >
            {children}
        </span>
    );
};

const getTooltipPosition = (targetRef: React.RefObject<HTMLElement>) => {
    if (!targetRef || !targetRef.current) return undefined;

    let top = 0;
    let left = 0;
    const OFFSET_HEIGHT = 7;

    const targetRect = targetRef.current.getBoundingClientRect();
    top = targetRect.top + targetRect.height + OFFSET_HEIGHT + window.scrollY;
    left = targetRect.left + window.scrollX;

    return { top, left };
};

export interface TooltipWrappedProps {
    children: React.ReactNode;
    tooltip: React.ReactNode;
    tooltipClassname?: string;
    wrapper: "button" | "div" | "td";
    wrapperClassname?: string;
    className?: string;
}

const TooltipWrapper = ({
    children,
    tooltip,
    tooltipClassname,
    wrapperClassname,
    wrapper,
    className,
    ...props
}: TooltipWrappedProps & TooltipTriggerProps & HTMLAttributes<HTMLElement>) => {
    let state = useTooltipTriggerState(props);
    let ref = React.useRef(null);
    let overlayRef = React.useRef(null);
    const contentWrapper = { wrapper };

    // Get props for the trigger and its tooltip
    let { triggerProps, tooltipProps } = useTooltipTrigger(props, state, ref);
    let { overlayProps } = useOverlayPosition({ targetRef: ref, overlayRef });

    return (
        <div className={cn("ds-tooltip__container", className)}>
            <contentWrapper.wrapper
                ref={ref}
                className={wrapperClassname}
                {...triggerProps}
                {...props}
            >
                {children}
            </contentWrapper.wrapper>
            {state.isOpen && (
                <Overlay {...overlayProps}>
                    <Tooltip
                        state={state}
                        className={tooltipClassname}
                        tooltipRef={overlayRef}
                        targetRef={ref}
                        {...tooltipProps}
                    >
                        {tooltip}
                    </Tooltip>
                </Overlay>
            )}
        </div>
    );
};

export { Tooltip, TooltipWrapper };
